import { createColumnHelper } from '@tanstack/react-table';
import { DocumentPromptPreview } from '@app/types/document-prompt-preview';
import { TableMetaCustom } from '@app/types/overrides/table-meta';
import MainTableRowActions from '@components/MainTable/MainTableRowActions';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { DocumentPromptPreviewStatus } from '@utils/enums.ts';
import { Typography } from '@mui/material';
import { dateFromString } from '@utils/utils.ts';
import { StatusTag } from '@shared/components';
import TruncatedTextWithTooltip from '@components/TruncatedTextWithTooltip';

const columnHelper = createColumnHelper<DocumentPromptPreview>();

export const documentPromptPreviewTableColumns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    enableSorting: true,
    size: 100,
    enableResizing: false,
  }),
  columnHelper.accessor('user_content', {
    id: 'user_content',
    header: 'User Content',
    enableSorting: true,
    size: 200,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <TruncatedTextWithTooltip text={original.user_content} maxLength={50} />
    ),
  }),
  columnHelper.accessor('generation_prompt', {
    id: 'generation_prompt',
    header: 'Generation Prompt',
    enableSorting: true,
    size: 200,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <TruncatedTextWithTooltip
        text={original.generation_prompt}
        maxLength={50}
      />
    ),
  }),
  columnHelper.accessor('extraction_prompt', {
    id: 'extraction_prompt',
    header: 'Extraction Prompt',
    enableSorting: true,
    size: 200,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <TruncatedTextWithTooltip
        text={original.extraction_prompt}
        maxLength={50}
      />
    ),
  }),
  columnHelper.accessor('created_at', {
    id: 'created_at',
    header: 'Created At',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">
        {dateFromString(new Date(original.created_at))}
      </Typography>
    ),
  }),
  columnHelper.accessor('updated_at', {
    id: 'updated_at',
    header: 'Updated At',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">
        {dateFromString(new Date(original.updated_at))}
      </Typography>
    ),
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => <StatusTag status={original.status} />,
  }),
  columnHelper.accessor('id', {
    id: 'id',
    header: 'Actions',
    enableSorting: false,
    enableResizing: false,
    cell: ({
      row: { original },
      table: {
        options: { meta },
      },
    }) => {
      const tableMeta = meta as TableMetaCustom<typeof original>;

      return (
        <MainTableRowActions>
          {original.status !== DocumentPromptPreviewStatus.PENDING && (
            <>
              <Tooltip title="Edit Document">
                <IconButton
                  onClick={() => {
                    tableMeta?.handleEditRow &&
                      tableMeta?.handleEditRow(original);
                  }}
                >
                  <IconPencil />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Document">
                <IconButton
                  onClick={() => {
                    tableMeta?.handleDeleteRow &&
                      tableMeta?.handleDeleteRow(original);
                  }}
                >
                  <IconTrash />
                </IconButton>
              </Tooltip>
            </>
          )}
        </MainTableRowActions>
      );
    },
  }),
];
